import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const MAX_IMAGES = 20;
const MAX_MB = 15;
const MIN_LONG = 1280;
const MIN_SHORT = 720;
const TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const isHeic = (f) => /image\/hei[cf]/.test(f.type) || /\.(heic|heif)$/i.test(f.name);

const readDimensions = (url) => new Promise((resolve) => {
  const img = new window.Image();
  img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
  img.onerror = () => resolve(null);
  img.src = url;
});

const checkFile = async (file) => {
  if (!TYPES.includes(file.type) && !isHeic(file)) return { error: 'unsupported file type (use JPEG, PNG, WebP or HEIC)' };
  if (file.size > MAX_MB * 1024 * 1024) return { error: `file is ${(file.size / 1048576).toFixed(1)}MB, maximum is ${MAX_MB}MB` };
  const preview = URL.createObjectURL(file);
  const dims = await readDimensions(preview);
  // HEIC often can't be decoded by the browser; the server checks its resolution on upload
  if (!dims && !isHeic(file)) { URL.revokeObjectURL(preview); return { error: 'file could not be read as an image' }; }
  if (dims && (Math.max(dims.width, dims.height) < MIN_LONG || Math.min(dims.width, dims.height) < MIN_SHORT)) {
    URL.revokeObjectURL(preview);
    return { error: `resolution ${dims.width}x${dims.height} is below the ${MIN_LONG}x${MIN_SHORT} minimum` };
  }
  return { preview, canPreview: Boolean(dims) };
};

const Thumb = ({ item, index, isCover, onRemove, disabled }) => (
  <div className="relative rounded-lg overflow-hidden border bg-white" data-testid={`image-preview-${index}`}>
    <div className="aspect-video bg-gray-100 relative">
      {item.canPreview ? (
        <img src={item.preview} alt={item.file.name} className="w-full h-full object-cover" data-testid={`image-preview-thumb-${index}`} />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
          <i className="fas fa-file-image text-3xl"></i>
          <span className="text-xs mt-1 px-2 truncate max-w-full">{item.file.name}</span>
        </div>
      )}
      {isCover && <span className="absolute bottom-1 left-1 bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded">Cover</span>}
      {!disabled && (
        <button type="button" onClick={() => onRemove(item.id)} data-testid={`remove-image-${index}`}
          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
          <i className="fas fa-times"></i>
        </button>
      )}
    </div>
    {item.status !== 'pending' && (
      <div className="p-1.5" data-testid={`image-upload-status-${index}`}>
        {item.status === 'error'
          ? <p className="text-[11px] text-red-600">{item.error}</p>
          : <Progress value={item.status === 'done' ? 100 : item.progress} className="h-1.5" />}
      </div>
    )}
  </div>
);

const ImageUploader = forwardRef(({ existingCount = 0, ownerId = '', maxImages = MAX_IMAGES, disabled = false }, ref) => {
  const [items, setItems] = useState([]);
  const [rejected, setRejected] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const inputRef = useRef(null);
  const remaining = maxImages - existingCount - items.length;

  const patch = (id, changes) => setItems(prev => prev.map(i => (i.id === id ? { ...i, ...changes } : i)));

  const handleSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const accepted = [];
    const errors = [];
    let slots = remaining;
    for (const file of files) {
      if (slots <= 0) { errors.push({ name: file.name, reason: `limit of ${maxImages} images reached` }); continue; }
      const res = await checkFile(file);
      if (res.error) { errors.push({ name: file.name, reason: res.error }); continue; }
      accepted.push({ id: `${Date.now()}-${Math.random()}`, file, preview: res.preview, canPreview: res.canPreview, status: 'pending', progress: 0 });
      slots -= 1;
    }
    setItems(prev => [...prev, ...accepted]);
    setRejected(errors);
    if (errors.length) toast.error(`${errors.length} photo(s) not added — see the reasons below the photos`);
  };

  const remove = (id) => setItems(prev => {
    const item = prev.find(i => i.id === id);
    if (item) URL.revokeObjectURL(item.preview);
    return prev.filter(i => i.id !== id);
  });

  const uploadOne = async (item) => {
    if (item.status === 'done') return item.url;
    patch(item.id, { status: 'uploading', progress: 0, error: '' });
    const body = new FormData();
    body.append('file', item.file);
    body.append('ownerId', ownerId);
    try {
      const { data } = await axios.post(`${API}/uploads/listing-image`, body, {
        onUploadProgress: (ev) => ev.total && patch(item.id, { progress: Math.round((ev.loaded / ev.total) * 100) }),
      });
      patch(item.id, { status: 'done', url: data.url });
      return data.url;
    } catch (err) {
      patch(item.id, { status: 'error', error: err.response?.data?.detail || `${item.file.name}: upload failed, press Submit to retry` });
      return null;
    }
  };

  useImperativeHandle(ref, () => ({
    // Uploads 3 at a time, keeps order; returns { urls, failed }
    uploadAll: async () => {
      const list = itemsRef.current;
      const urls = new Array(list.length);
      let next = 0;
      const worker = async () => { while (next < list.length) { const i = next++; urls[i] = await uploadOne(list[i]); } };
      await Promise.all([worker(), worker(), worker()]);
      return { urls: urls.filter(Boolean), failed: urls.filter(u => !u).length };
    },
  }));

  return (
    <div className="space-y-4">
      <div
        onClick={() => !disabled && remaining > 0 && inputRef.current?.click()}
        data-testid="image-dropzone"
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          remaining <= 0 || disabled ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
            : 'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50 cursor-pointer'}`}
      >
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
          onChange={handleSelect} className="hidden" data-testid="image-file-input" disabled={disabled || remaining <= 0} />
        <i className="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-3"></i>
        <p className="text-gray-700 font-medium" data-testid="image-counter">
          {items.length > 0 ? `${items.length} new photo(s) added — tap to add more` : 'Tap to add photos'}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          JPEG, PNG, WebP or HEIC • min {MIN_LONG}x{MIN_SHORT} (any orientation) • max {MAX_MB}MB each • {Math.max(remaining, 0)} of {maxImages} slots left
        </p>
      </div>

      {rejected.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="image-rejections">
          {rejected.map((r, i) => (
            <p key={i} className="text-sm text-red-700"><i className="fas fa-exclamation-circle mr-1"></i><strong>{r.name}</strong>: {r.reason}</p>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((item, index) => (
            <Thumb key={item.id} item={item} index={index} isCover={existingCount === 0 && index === 0} onRemove={remove} disabled={disabled} />
          ))}
        </div>
      )}
    </div>
  );
});

export default ImageUploader;
